import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AdminFormPage } from "@components/CatalogIndex";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { PaymentTermForm } from "../PaymentTermForm/PaymentTermForm";
import { usePaymentTermMutations } from "../api";
import { emptyPaymentTermForm } from "../types";
import type { PaymentTermFormValues } from "../types";

const FORM_ID = "payment-term-form";

export const New = (): JSX.Element => {
  const navigate = useNavigate();
  const { create } = usePaymentTermMutations();
  const [submitting, setSubmitting] = useState(false);
  // Remounting the form via key resets it after "Save and Add Another".
  const [formKey, setFormKey] = useState(0);
  const [toast, setToast] = useState<{
    variant: "success" | "danger";
    text: string;
  } | null>(null);
  const nextAction = useRef<"edit" | "new">("edit");

  const handleSubmit = async (values: PaymentTermFormValues) => {
    setSubmitting(true);
    const addAnother = nextAction.current === "new";
    try {
      const result: any = await create(values);
      const newId = result?.data?.id;
      if (addAnother) {
        setFormKey((k) => k + 1);
        setToast({ variant: "success", text: `'${values.name}' saved` });
        setSubmitting(false);
      } else {
        navigate(newId ? `../${newId}/edit` : "..", {
          relative: "path",
          state: {
            toast: { variant: "success", text: `'${values.name}' saved` },
          },
        });
      }
    } catch (err) {
      console.error("Failed to create payment term", err);
      setToast({ variant: "danger", text: "Could not save payment term." });
      setSubmitting(false);
    } finally {
      nextAction.current = "edit";
    }
  };

  const triggerAddAnother = () => {
    nextAction.current = "new";
    (
      document.getElementById(FORM_ID) as HTMLFormElement | null
    )?.requestSubmit();
  };

  return (
    <>
      <AdminFormPage
        title="Add a Payment Term"
        formId={FORM_ID}
        onCancel={() => navigate("..")}
        onSaveAndAddAnother={triggerAddAnother}
        isSubmitting={submitting}
      >
        <PaymentTermForm
          key={formKey}
          id={FORM_ID}
          initialValues={emptyPaymentTermForm}
          isSubmitting={submitting}
          onSubmit={handleSubmit}
          onInvalid={(text) => {
            // If validation blocks a "Save and Add Another" submit, handleSubmit
            // never runs to reset the ref — so reset it here. Otherwise a later
            // plain Save would wrongly "add another" instead of navigating.
            nextAction.current = "edit";
            setToast({ variant: "danger", text });
          }}
        />
      </AdminFormPage>
      {toast && (
        <ToastAlert
          variant={toast.variant}
          text={toast.text}
          onClose={() => setToast(null)}
        />
      )}
    </>
  );
};

export default New;
